import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Save, PlusCircle, Syringe as SyringeIcon, Calculator as CalcIcon, FlaskConical } from "lucide-react";
import "./PeptideCalculatorPro.css";

/**
 * Visual design comes from the user-supplied HTML calculator (dark theme,
 * animated syringe draw). We wrap that visual in React so we can preserve the
 * existing logging affordances (Log dose to shots, Save to catalog, Save vial).
 *
 * Props mirror `PeptideCalculatorInner`:
 *   - embedded: bool (ignored — always renders same shell)
 *   - preset:   { inputs: { peptide_name, amount_mg, bac_ml, dose_value, dose_unit, syringe_size } }
 *   - onSaved:  callback after Save-to-catalog
 */
export default function PeptideCalculatorPro({ preset = null, onSaved }) {
  // Form state (matches the HTML calculator's inputs)
  const [vialValue, setVialValue] = useState("10");
  const [vialUnit, setVialUnit] = useState("mg");
  const [waterMl, setWaterMl] = useState("2");
  const [targetValue, setTargetValue] = useState("250");
  const [targetUnit, setTargetUnit] = useState("mcg");
  const [error, setError] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [animatedDraw, setAnimatedDraw] = useState(0); // 0..100 for CSS

  // Logging state (kept from old calculator)
  const [peptides, setPeptides] = useState([]);
  const [peptideName, setPeptideName] = useState("");
  const [logging, setLogging] = useState(false);
  const [savingCatalog, setSavingCatalog] = useState(false);
  const [savingVial, setSavingVial] = useState(false);

  const rootRef = useRef(null);
  const animTimer = useRef(null);

  useEffect(() => {
    api.get("/peptides").then(({ data }) => setPeptides(data || [])).catch(() => {});
  }, []);

  // Hydrate from catalog preset when the user clicks a saved calc.
  useEffect(() => {
    if (!preset) return;
    const inp = preset.inputs || {};
    if (inp.peptide_name != null) setPeptideName(inp.peptide_name);
    if (inp.amount_mg != null) { setVialValue(String(inp.amount_mg)); setVialUnit("mg"); }
    if (inp.bac_ml != null) setWaterMl(String(inp.bac_ml));
    if (inp.dose_value != null) setTargetValue(String(inp.dose_value));
    if (inp.dose_unit) setTargetUnit(inp.dose_unit === "mg" ? "mg" : "mcg");
  }, [preset]);

  // ----- Pure arithmetic (mirrors the uploaded HTML's JS exactly) -----
  const calc = useMemo(() => {
    const vial = parseFloat(vialValue);
    const water = parseFloat(waterMl);
    const target = parseFloat(targetValue);
    if (![vial, water, target].every((n) => Number.isFinite(n) && n > 0)) {
      return null;
    }
    if (water > 10000) return { invalid: "Diluent volume is outside the calculator range." };
    const vialMcg = vialUnit === "mg" ? vial * 1000 : vial;
    const targetMcg = targetUnit === "mg" ? target * 1000 : target;
    const concMcg = vialMcg / water;
    const concMg = concMcg / 1000;
    const drawMl = targetMcg / concMcg;
    const units = drawMl * 100;
    if (!Number.isFinite(drawMl) || drawMl <= 0 || drawMl > 10000) {
      return { invalid: "The entered values produce an invalid liquid volume. Check the amount, diluent, and entered quantity." };
    }
    return { vial, vialMcg, water, target, targetMcg, concMcg, concMg, drawMl, units, vialUnit, targetUnit };
  }, [vialValue, vialUnit, waterMl, targetValue, targetUnit]);

  const trim = (n, d = 6) => Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
  const fixed = (n, d) => Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

  const draw = showResult && calc && !calc.invalid ? calc : null;

  const onSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!calc) return setError("Enter a valid positive number for each field.");
    if (calc.invalid) return setError(calc.invalid);
    // Reset animation
    setShowResult(true);
    setIsCalculating(true);
    setAnimatedDraw(0);
    // Two RAFs to trigger the CSS transition (mirrors the HTML's rAF chain).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setAnimatedDraw(Math.max(0, Math.min(100, calc.units)));
      });
    });
    if (animTimer.current) window.clearTimeout(animTimer.current);
    animTimer.current = window.setTimeout(() => setIsCalculating(false), 2300);
  };

  const onReset = () => {
    setVialValue(""); setWaterMl(""); setTargetValue("");
    setVialUnit("mg"); setTargetUnit("mcg");
    setError(""); setShowResult(false); setAnimatedDraw(0);
    if (animTimer.current) window.clearTimeout(animTimer.current);
    setIsCalculating(false);
  };

  // ---------- Logging (preserved) ----------
  const logDose = async () => {
    if (!draw) return toast.error("Calculate first.");
    if (!peptideName) return toast.error("Pick a peptide first so we know what to log.");
    setLogging(true);
    try {
      // Backend expects dose in mcg or mg; we pass target as-is.
      const notes = `Reconstitution: ${trim(draw.vial, 4)} ${draw.vialUnit} / ${trim(draw.water, 4)} mL → ${fixed(draw.units, 2)} u (draw ${fixed(draw.drawMl, 4)} mL)`;
      const res = await api.post("/shots", {
        peptide_name: peptideName,
        dose_amount: parseFloat(targetValue),
        dose_unit: targetUnit,
        injection_site: null,
        notes,
        taken_at: new Date().toISOString(),
        interval_hours: 24,
      });
      const d = res.data || {};
      if (d.deducted_vial_id) {
        const rem = Number(d.deducted_vial_remaining_mg ?? 0);
        const total = Number(d.deducted_vial_amount_mg ?? 0);
        toast.success(`Dose logged • ${d.deducted_vial_peptide_name} → ${rem.toFixed(2)}/${total.toFixed(2)} mg left`);
      } else {
        toast.success("Dose logged");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLogging(false);
    }
  };

  const saveVial = async () => {
    if (!draw) return toast.error("Calculate first.");
    if (!peptideName) return toast.error("Pick a peptide first so this vial is filed correctly.");
    setSavingVial(true);
    try {
      const amountMg = draw.vialUnit === "mg" ? draw.vial : draw.vial / 1000;
      await api.post("/vials", {
        peptide_name: peptideName,
        amount_mg: amountMg,
        bac_water_ml: draw.water,
        purity_pct: 100,
        quantity: 1,
      });
      toast.success("Vial saved to inventory");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingVial(false);
    }
  };

  const saveToCatalog = async () => {
    if (!draw) return toast.error("Calculate first.");
    setSavingCatalog(true);
    try {
      const amountMg = draw.vialUnit === "mg" ? draw.vial : draw.vial / 1000;
      await api.post("/calc-history", {
        calc_type: "peptide",
        label: peptideName || null,
        inputs: {
          peptide_name: peptideName || null,
          amount_mg: amountMg,
          bac_ml: draw.water,
          dose_value: parseFloat(targetValue),
          dose_unit: targetUnit,
          syringe_size: 100,
          split_enabled: false,
        },
        outputs: {
          concentration_mcg_per_ml: draw.concMcg,
          draw_ml: draw.drawMl,
          draw_units: draw.units,
          dose_mcg: draw.targetMcg,
        },
      });
      toast.success("Saved to catalog");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingCatalog(false);
    }
  };

  // ---------- Display strings ----------
  const drawUnits = draw ? fixed(draw.units, 2) : "0.00";
  const concMgTxt = draw ? fixed(draw.concMg, 6) : "0.000000";
  const concMcgTxt = draw ? fixed(draw.concMcg, 3) : "0.000";
  const volumeTxt = draw ? fixed(draw.drawMl, 4) : "0.0000";
  const volumeUnitsTxt = draw ? fixed(draw.units, 2) : "0.00";
  const targetDisplay = draw ? trim(draw.target) : "0";
  const vialLabel = draw ? `${trim(draw.vial, 4)} ${draw.vialUnit}` : "0 mg";
  const targetTag = draw ? `${fixed(draw.units, 2)} U` : "0.00 U";
  const caption = !showResult
    ? "Ready at 0 units"
    : isCalculating
      ? `Drawing from 0 to ${drawUnits} units`
      : `Draw shown at ${drawUnits} units`;
  const howVial = draw ? `${trim(draw.vial, 4)} ${draw.vialUnit} with ${trim(draw.water, 4)} mL diluent.` : "0 mg with 0 mL diluent.";
  const howConc = draw ? `${concMgTxt} mg/mL.` : "0.000000 mg/mL.";
  const howTarget = draw ? `${trim(draw.target)} ${draw.targetUnit} equals ${volumeTxt} mL.` : "0 mcg equals 0.0000 mL.";
  const howDraw = draw
    ? `fluid travels down from the vial through the needle and the syringe moves from zero to ${drawUnits} on the U-100 scale.`
    : "the syringe is at the zero mark until Calculate is pressed.";
  const detailConc = draw ? `${trim(draw.vial)} ${draw.vialUnit} ÷ ${trim(draw.water)} mL = ${concMgTxt} mg/mL` : "0 mg ÷ 0 mL = 0.000000 mg/mL";
  const detailVol = draw ? `${trim(draw.target)} ${draw.targetUnit} → ${volumeTxt} mL → ${drawUnits} U-100 units` : "0 mcg → 0.0000 mL → 0.00 U-100 units";
  const overflow = draw && draw.units > 100;

  const shellStyle = { "--pep138-draw": `${animatedDraw.toFixed(2)}%` };

  return (
    <div
      ref={rootRef}
      className={`pep138-shell fg-peptide-calculator ${isCalculating ? "is-calculating" : ""}`}
      style={shellStyle}
      data-testid="peptide-calculator-pro"
    >
      <div className="pep138-head">
        <div>
          <div className="pep138-eyebrow">// PRECISION DOSING</div>
          <h2><span className="pep138-icon"><CalcIcon size={16} /></span>Peptide Calculator</h2>
          <p>Concentration and liquid-volume math from values you enter.</p>
        </div>
        <div className="pep138-head-note">Arithmetic · logging optional</div>
      </div>

      <div className="pep138-main">
        {/* -------- Inputs -------- */}
        <section className="pep138-panel pep138-input-panel">
          <div className="pep138-panel-title"><span>//</span><strong>Calculator Inputs</strong></div>
          <div className="pep138-safety">
            <strong>Research calculation only.</strong> Enter a quantity you already selected. The calculator does not choose a peptide, dose, treatment, route, protocol, or schedule. Pick a peptide below only if you'd like to log this dose after calculating.
          </div>
          <form className="pep138-form" onSubmit={onSubmit} autoComplete="off">
            {/* Peptide picker (for logging) */}
            <div className="pep138-field">
              <label>Peptide (optional — enables logging)</label>
              <Select value={peptideName || "__none__"} onValueChange={(v) => setPeptideName(v === "__none__" ? "" : v)}>
                <SelectTrigger className="pep138-select-trigger" data-testid="calc-pro-peptide">
                  <SelectValue placeholder="None (calc only)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (calc only)</SelectItem>
                  {peptides.map((p) => (
                    <SelectItem key={p.id || p.name} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <small>Skip to calculate only. Pick one to log a dose or save a vial after.</small>
            </div>
            <div className="pep138-field">
              <label>Amount in vial</label>
              <div className="pep138-input-row">
                <input
                  className="pep138-number-entry"
                  type="text"
                  inputMode="decimal"
                  value={vialValue}
                  onChange={(e) => setVialValue(e.target.value)}
                  placeholder="Type amount"
                  aria-label="Amount in vial"
                  required
                  data-testid="calc-pro-vial-value"
                />
                <select
                  value={vialUnit}
                  onChange={(e) => setVialUnit(e.target.value)}
                  aria-label="Vial amount unit"
                  data-testid="calc-pro-vial-unit"
                >
                  <option value="mg">mg</option>
                  <option value="mcg">mcg</option>
                </select>
              </div>
              <small>Type the vial amount directly, then choose mg or mcg.</small>
            </div>
            <div className="pep138-field">
              <label>Diluent added</label>
              <div className="pep138-input-row">
                <input
                  className="pep138-number-entry"
                  type="text"
                  inputMode="decimal"
                  value={waterMl}
                  onChange={(e) => setWaterMl(e.target.value)}
                  placeholder="Type amount"
                  aria-label="Diluent added in mL"
                  required
                  data-testid="calc-pro-water"
                />
                <span className="pep138-unit-box">mL</span>
              </div>
              <small>Type the liquid volume directly.</small>
            </div>
            <div className="pep138-field">
              <label>Entered quantity to measure</label>
              <div className="pep138-input-row">
                <input
                  className="pep138-number-entry"
                  type="text"
                  inputMode="decimal"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  placeholder="Type amount"
                  aria-label="Entered quantity to measure"
                  required
                  data-testid="calc-pro-target-value"
                />
                <select
                  value={targetUnit}
                  onChange={(e) => setTargetUnit(e.target.value)}
                  aria-label="Entered quantity unit"
                  data-testid="calc-pro-target-unit"
                >
                  <option value="mcg">mcg</option>
                  <option value="mg">mg</option>
                </select>
              </div>
              <small>Type your entered quantity directly, then choose mcg or mg.</small>
            </div>
            <div className="pep138-actions">
              <button className="pep138-calc-button" type="submit" data-testid="calc-pro-calc">
                <CalcIcon size={14} /> Calculate &amp; show draw
              </button>
              <button className="pep138-reset-button" type="button" onClick={onReset} data-testid="calc-pro-reset">Reset</button>
            </div>
            {error && (
              <div className="pep138-form-error" role="alert" data-testid="calc-pro-error">{error}</div>
            )}
          </form>
        </section>

        {/* -------- Results -------- */}
        <section className="pep138-panel pep138-results-panel">
          <div className="pep138-panel-title"><span>//</span><strong>Calculation Results</strong></div>
          <div>
            <div className="pep138-result-layout">
              <div className="pep138-metrics">
                <div className="pep138-metric">
                  <span>DRAW MARK</span>
                  <strong><b data-testid="calc-pro-draw-units">{drawUnits}</b> <em>Units</em></strong>
                  <small>U-100 equivalent</small>
                  {overflow && (
                    <small className="pep138-overflow-note">
                      The arithmetic is above 100 units. The syringe illustration stops at 100 while the result keeps the actual calculated number.
                    </small>
                  )}
                </div>
                <div className="pep138-metric">
                  <span>ENTERED QUANTITY</span>
                  <strong><b>{targetDisplay}</b> <em>{draw ? draw.targetUnit : "mcg"}</em></strong>
                  <small>Supplied by you</small>
                </div>
                <div className="pep138-metric">
                  <span>CONCENTRATION</span>
                  <strong><b>{concMgTxt}</b> <em>mg/mL</em></strong>
                  <small><span>{concMcgTxt}</span> mcg/mL</small>
                </div>
                <div className="pep138-metric">
                  <span>LIQUID VOLUME</span>
                  <strong><b>{volumeTxt}</b> <em>mL</em></strong>
                  <small><span>{volumeUnitsTxt}</span> U-100 units</small>
                </div>
                <div className="pep138-research-only">
                  <strong>△ Research use only</strong>
                  <small>Illustration and arithmetic only. Not an administration or device-use instruction.</small>
                </div>
              </div>

              {/* -------- Syringe illustration -------- */}
              <div className="pep138-visual-wrap">
                <div className="pep138-draw-rig" aria-label="Illustration of an upside-down vial, a needle, and a U-100 syringe">
                  <div className="pep138-vial">
                    <div className="pep138-vial-body">
                      <div className="pep138-vial-label"><strong>VIAL</strong><small>{vialLabel}</small></div>
                      <div className="pep138-vial-liquid"></div>
                    </div>
                    <div className="pep138-stopper"></div>
                  </div>
                  <div className="pep138-needle"><span></span><i aria-hidden="true"></i></div>
                  <div className="pep138-syringe">
                    <div className="pep138-barrel">
                      <div className="pep138-liquid"></div>
                      <div className="pep138-ticks"></div>
                      {[10,20,30,40,50,60,70,80,90,100].map((n) => (
                        <span key={n} className="pep138-num" style={{ "--n": n / 100 }}>{n}</span>
                      ))}
                      <div className="pep138-black-stopper"></div>
                      <div className="pep138-target-tag">{targetTag}</div>
                    </div>
                    <div className="pep138-plunger-rod"></div>
                    <div className="pep138-thumb"></div>
                  </div>
                  <div className="pep138-anim-caption"><span></span><b>{caption}</b></div>
                </div>
              </div>

              {/* -------- How it's shown -------- */}
              <div className="pep138-how">
                <h3>How the math is shown</h3>
                <ol>
                  <li><span>1</span><p><strong>Entered vial:</strong> <b>{howVial}</b></p></li>
                  <li><span>2</span><p><strong>Concentration:</strong> <b>{howConc}</b></p></li>
                  <li><span>3</span><p><strong>Entered quantity:</strong> <b>{howTarget}</b></p></li>
                  <li><span>4</span><p><strong>Illustration:</strong> <b>{howDraw}</b></p></li>
                </ol>
              </div>
            </div>

            <div className="pep138-details">
              <div><span>CONCENTRATION CALCULATION</span><strong>{detailConc}</strong></div>
              <div><span>LIQUID-VOLUME CALCULATION</span><strong>{detailVol}</strong></div>
              <div><span>U-100 SCALE</span><strong>100 units = 1.00 mL · 50 units = 0.50 mL · 10 units = 0.10 mL</strong></div>
            </div>

            {/* -------- Logging actions (kept from old calc) -------- */}
            <div className="pep138-log-actions" data-testid="calc-pro-log-actions">
              <button
                type="button"
                className="pep138-log-button pep138-log-primary"
                onClick={logDose}
                disabled={!draw || logging || !peptideName}
                data-testid="calc-pro-log-dose"
                title={!peptideName ? "Pick a peptide above to enable logging" : "Log this dose to your history"}
              >
                <SyringeIcon size={14} /> {logging ? "Logging…" : "Log this dose"}
              </button>
              <button
                type="button"
                className="pep138-log-button"
                onClick={saveVial}
                disabled={!draw || savingVial || !peptideName}
                data-testid="calc-pro-save-vial"
                title={!peptideName ? "Pick a peptide above to save a vial" : "Save this as a vial in your inventory"}
              >
                <PlusCircle size={14} /> {savingVial ? "Saving…" : "Save as vial"}
              </button>
              <button
                type="button"
                className="pep138-log-button"
                onClick={saveToCatalog}
                disabled={!draw || savingCatalog}
                data-testid="calc-pro-save-catalog"
                title="Save this calculation to your catalog for later reuse"
              >
                <Save size={14} /> {savingCatalog ? "Saving…" : "Save to catalog"}
              </button>
            </div>

            <div className="pep138-browser-note">
              The math runs in your browser and stays on this page. Nothing is saved unless you tap Log this dose, Save as vial, or Save to catalog.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
